/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

export type DeviceSize = 'mobile' | 'tablet' | 'desktop';

export type IconState = 'base' | 'neutral' | 'selected' | 'orange';

/**
 * Maps vehicle type names to their corresponding icon paths with responsive variants.
 * Supports both English and Spanish vehicle type names for US and MX sites.
 * Handles case-insensitive matching and defaults to car icon for unknown types.
 * @param vehicleTypeName - The vehicle type (e.g., 'Motorcycle', 'Motocicletas', 'Recreation', 'Recreativos', 'Car & Truck', 'Autos y Camionetas')
 * @param size - Device size for responsive car icons
 * @param iconState - Icon state: 'base', 'neutral' (gray), 'selected' (with checkmark), or 'orange' (branded)
 */
export const getVehicleTypeIcon = (
  vehicleTypeName: string | undefined,
  size: DeviceSize = 'desktop',
  iconState: IconState = 'selected',
): string => {
  if (!vehicleTypeName) {
    // Default to car icon for undefined/null
    return getCarIcon(size, iconState);
  }

  const normalizedTypeName = vehicleTypeName.toLowerCase().trim();

  // Support both English and Spanish vehicle type names
  if (
    normalizedTypeName.includes('motorcycle') ||
    normalizedTypeName === 'moto' ||
    normalizedTypeName.includes('motocicleta')
  ) {
    if (iconState === 'base') {
      return '/images/ymmeAces/ymme-moto.svg';
    }
    if (iconState === 'neutral') {
      return '/images/ymmeAces/ymme-moto-neutral.svg';
    }
    if (iconState === 'orange') {
      return '/images/ymmeAces/ymme-moto-orange.svg';
    }
    return '/images/ymmeAces/ymme-moto-selected.svg';
  }

  if (
    normalizedTypeName.includes('recreation') ||
    normalizedTypeName === 'rec' ||
    normalizedTypeName.includes('recreativo')
  ) {
    if (iconState === 'base') {
      return '/images/ymmeAces/ymme-recreation.svg';
    }
    if (iconState === 'neutral') {
      return '/images/ymmeAces/ymme-recreation-neutral.svg';
    }
    if (iconState === 'orange') {
      return '/images/ymmeAces/ymme-recreation-orange.svg';
    }
    return '/images/ymmeAces/ymme-recreation-selected.svg';
  }

  // Default to car icon for unknown types and for 'Car & Truck'
  return getCarIcon(size, iconState);
};

/**
 * Returns the appropriate car icon based on device size and icon state
 */
const getCarIcon = (size: DeviceSize, iconState: IconState): string => {
  // Orange state uses the branded orange car icon
  if (iconState === 'orange') {
    return '/images/vehicle-new-orange.svg';
  }

  // Neutral and base states use the generic car icon
  if (iconState === 'neutral') {
    return '/images/myVehicle.svg';
  } else if (iconState === 'base') {
    return '/images/vehicle-new.svg';
  }

  // Selected state uses device-specific icons with checkmark
  const carIcons: Record<DeviceSize, string> = {
    mobile: '/images/icon-vehicle-selected-m.svg',
    tablet: '/images/icon-vehicle-selected-t.svg',
    desktop: '/images/icon-vehicle-selected-d.svg',
  };

  return carIcons[size];
};
